import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { TrendingUp, Users, FileText, Target } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#C9A84C", "#7C6B2F", "#E8CA6E", "#9E8340"];

export default function AIDashboard() {
  const { clientId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState("");

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/clients/${clientId}/dashboard-data`, { withCredentials: true }),
      axios.get(`${API}/clients/${clientId}`, { withCredentials: true })
    ]).then(([dashRes, clientRes]) => {
      setData(dashRes.data);
      setClientName(clientRes.data.business_name);
    }).catch(console.error).finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin"></div></div>;
  if (!data || Object.keys(data).length === 0) return <div className="text-center py-20 text-[#9999B3]">No dashboard data available</div>;

  const stats = [
    { label: "Total Leads", value: data.total_leads, icon: Users, color: "text-[#C9A84C]" },
    { label: "Form Submissions", value: data.form_submissions, icon: FileText, color: "text-blue-400" },
    { label: "Conversion Rate", value: `${data.conversion_rate}%`, icon: Target, color: "text-green-400" },
    { label: "This Month Leads", value: data.leads?.[data.leads.length - 1]?.count || 0, icon: TrendingUp, color: "text-purple-400" },
  ];

  return (
    <div data-testid="ai-dashboard-page">
      <h1 className="text-2xl font-bold text-white mb-1">{clientName} — AI Dashboard</h1>
      <p className="text-[#9999B3] text-sm mb-6">Lead tracking, traffic, and key metrics</p>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {stats.map((s, i) => (
          <div key={i} className="bg-[#12121F] border border-[#252540] rounded-lg p-4" data-testid={`kpi-${i}`}>
            <div className="flex items-center gap-2 mb-2"><s.icon className={`w-4 h-4 ${s.color}`}/><span className="text-[#6B6B85] text-xs">{s.label}</span></div>
            <p className="text-white text-2xl font-bold">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-[#12121F] border border-[#252540] rounded-lg p-5">
          <h3 className="text-white font-semibold text-sm mb-4">Leads Over Time</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.leads}>
              <CartesianGrid strokeDasharray="3 3" stroke="#252540"/>
              <XAxis dataKey="month" tick={{ fill: '#9999B3', fontSize: 11 }} axisLine={false}/>
              <YAxis tick={{ fill: '#9999B3', fontSize: 11 }} axisLine={false}/>
              <Tooltip contentStyle={{ backgroundColor: '#1A1A2E', border: '1px solid #252540', borderRadius: 8, color: '#fff', fontSize: 12 }}/>
              <Bar dataKey="count" fill="#C9A84C" radius={[4,4,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-[#12121F] border border-[#252540] rounded-lg p-5">
          <h3 className="text-white font-semibold text-sm mb-4">Website Traffic</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.traffic}>
              <CartesianGrid strokeDasharray="3 3" stroke="#252540"/>
              <XAxis dataKey="month" tick={{ fill: '#9999B3', fontSize: 11 }} axisLine={false}/>
              <YAxis tick={{ fill: '#9999B3', fontSize: 11 }} axisLine={false}/>
              <Tooltip contentStyle={{ backgroundColor: '#1A1A2E', border: '1px solid #252540', borderRadius: 8, color: '#fff', fontSize: 12 }}/>
              <Line type="monotone" dataKey="visitors" stroke="#C9A84C" strokeWidth={2} dot={{ fill: '#C9A84C', r: 4 }}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sources */}
      <div className="bg-[#12121F] border border-[#252540] rounded-lg p-5">
        <h3 className="text-white font-semibold text-sm mb-4">Top Lead Sources</h3>
        <div className="grid lg:grid-cols-2 gap-6 items-center">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={data.top_sources} dataKey="count" nameKey="source" cx="50%" cy="50%" outerRadius={70} innerRadius={40} paddingAngle={4}>
                {data.top_sources?.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: '#1A1A2E', border: '1px solid #252540', borderRadius: 8, color: '#fff', fontSize: 12 }}/>
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2">
            {data.top_sources?.map((s, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }}/><span className="text-[#C5C5D8] text-sm">{s.source}</span></div>
                <span className="text-white font-semibold text-sm">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
